import React, { useEffect, useState } from "react";
import moment from "moment";
import { Clock, Award, XCircle, ChevronDown, ChevronUp, Edit, CheckCircle } from "lucide-react";
import { api } from "../../../services/api";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import { enqueueSnackbar } from "notistack";
import { DetalheRelatorio } from "../../../pages/Informacoes/Treinamentos/components/DetalheRelatorio";
import { EditTreinamentoModal } from "../../../pages/Informacoes/Treinamentos/components/EditTreinamentoModal";
import { RelatarErrosModal } from "../../../pages/Informacoes/Treinamentos/components/RelatarErrosModal";
import { Skeleton, StaggerWrapper } from "../../../components/Skeleton";

interface RelatorioTreinamento {
  id: string;
  inicio_treinamento: string;
  termino_treinamento: string;
  treinamento: Treinamento;
  oficial_responsavel: string;
  criado_em: string;
  patente_treinador: string;
  treinador: Militar;
  patente_auxiliar: string;
  auxiliar: Militar;
  sala: string;
  aprovados: Aprovados[];
  reprovados: Reprovados[];
  qtd_treinados: number;
  qtd_aprovados: number;
  observacao: string;
  status: string;
  tipo_relatorio: string;
  militar_aceitou?: Militar;
}

interface Treinamento {
  id: string;
  treinamento: string;
}

interface Militar {
  id: string;
  nick: string;
}

interface Aprovados {
  nick: string;
}

interface Reprovados {
  nick: string;
  motivo: string;
  horario: string;
}

const getStatusIcon = (aprovados: number, reprovados: number) => {
  if (reprovados === 0) return <Award className="h-4 w-4 text-green-600" />;
  if (aprovados > reprovados) return <Award className="h-4 w-4 text-yellow-600" />;
  return <XCircle className="h-4 w-4 text-red-600" />;
};

const getStatusColor = (aprovados: number, reprovados: number) => {
  if (reprovados === 0) return "bg-green-100 text-green-800 border-green-200 dark:bg-green-900/40 dark:text-green-300 dark:border-green-700";
  if (aprovados > reprovados) return "bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-900/40 dark:text-yellow-300 dark:border-yellow-700";
  return "bg-red-100 text-red-800 border-red-200 dark:bg-red-900/40 dark:text-red-300 dark:border-red-700";
};

const getStatusBadge = (status: string, militar_aceitou?: Militar) => {
  if (status === "A") {
    return (
      <Badge className="bg-green-100 text-green-800 border-green-200 dark:bg-green-900/40 dark:text-green-300 dark:border-green-700">
        <CheckCircle className="h-3 w-3 mr-1" />
        Aceito{militar_aceitou && ` por ${militar_aceitou.nick}`}
      </Badge>
    );
  }
  return (
    <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-900/40 dark:text-yellow-300 dark:border-yellow-700">
      <Clock className="h-3 w-3 mr-1" />
      Pendente
    </Badge>
  );
};

export function RelatoriosTreinamento() {
  const [relatoriosTreinamento, setRelatoriosTreinamento] = useState<RelatorioTreinamento[]>([]);
  const [isVisible, setIsVisible] = useState<{ [key: string]: boolean }>({});
  const [loading, setLoading] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [errosModalOpen, setErrosModalOpen] = useState(false);
  const [relatorioSelecionado, setRelatorioSelecionado] = useState<RelatorioTreinamento | null>(null);
  const [ultimaAtualizacao, setUltimaAtualizacao] = useState<Date>(new Date());

  const carregarDados = async (isAutoUpdate = false) => {
    try {
      if (!isAutoUpdate) {
        setLoading(true);
      }

      // Brasil é permanentemente UTC-3 desde 2019 (sem horário de verão)
      const BRAZIL_OFFSET_MS = -3 * 60 * 60 * 1000;
      const now = new Date();
      const brazilNow = new Date(now.getTime() + BRAZIL_OFFSET_MS);

      // Verificar se ainda não bateu 4h no horário de Brasília
      const brazilHour = brazilNow.getUTCHours();
      const isBeforeFourAM = brazilHour < 4;

      // Início do período: hoje às 04:00 BRT (= 07:00 UTC) ou ontem se ainda antes das 4h
      const startBrazil = new Date(brazilNow);
      startBrazil.setUTCHours(4, 0, 0, 0);
      if (isBeforeFourAM) {
        startBrazil.setUTCDate(startBrazil.getUTCDate() - 1);
      }

      // Fim do período: início + 24h
      const endBrazil = new Date(startBrazil.getTime() + 24 * 60 * 60 * 1000);

      // Converter para UTC (BRT = UTC-3, então somar 3h para obter UTC)
      const inicioUTC = new Date(startBrazil.getTime() - BRAZIL_OFFSET_MS);
      const fimUTC = new Date(endBrazil.getTime() - BRAZIL_OFFSET_MS);

      const response = await api.get("/relatorioTreinamento", {
        params: {
          inicio_treinamento_inicio: inicioUTC.toISOString(),
          inicio_treinamento_fim: fimUTC.toISOString(),
        }
      });

      // Ordenar por inicio_treinamento, mais recentes primeiro
      const lista: RelatorioTreinamento[] = Array.isArray(response.data) ? response.data : (response.data.data ?? []);
      const sortedData = lista.sort((a: RelatorioTreinamento, b: RelatorioTreinamento) => {
        return moment(b.inicio_treinamento).valueOf() - moment(a.inicio_treinamento).valueOf();
      });

      // Verificar se há novos relatórios (por ID) e tocar som
      if (isAutoUpdate && relatoriosTreinamento.length > 0) {
        const idsAntigos = new Set(relatoriosTreinamento.map(r => r.id));
        const relatoriosNovos = sortedData.filter(r => !idsAntigos.has(r.id));

        if (relatoriosNovos.length > 0) {
          // Tocar som de notificação
          const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBSuBzvLZiTYIGGi77OeeSwwRUKfl8LZjHAU5kdbyy3orBSR3x/DdkD8KFF+06OzpVRQKRp/h8r1sIAUsgs/y2Yk2CBhpvO3onUwMEFCm5PC2YhwFN5HX8st6KwUldsfx3I8+ChRfsOjr51UUCkaQ4fK8ax8EKoLP8dmINggWZ7vu559NEAxPpuPwt2IdBTiR1/LLeisFI3bH8dyPPgoTX7Hn7OlUFApGn+DyvGsfBCmCzvHZiDYIF2e77uihTQwPT6bj8LdjHQU4ktjxy3orBSN2x/Hcjz0JFF+w5+zpVRMKRp/g8rpqHgQpgs/y2IY1BxdnvO7on04NEE+m5PG4Yx0FN5LY8cp5KgUidsjx3I88CRRQ6Ors6VQTCkag4PKwah4FK4PP8tiGNQcXZ7zu5585DRBPpuPxuGMcBTeT2PHKeSkFInbJ8dqOPAkUT+nm7OhUEwpGoODysGkeBS2Dz/PYhTQHFme97uegOA0PT6bj8bdjHAU2lNjxyHgoBSJ3yfHajjwJE0/o5uvoUxIKRp/f8q9oHgUsg8/y14YzBxVnve7onzUMEFCm4/G3YhwFNpPY8Mh4JwUidsnx2o47CRNP6OXs51MSCkSf4PKwZx0FLoLP8teGMwcVZ73t56A0DQ9Ppeby12EbBTaT2PG4YRgFJnnO8tuMNAoSUObm6eZOEAhEl+Tvq2MbBi+Bz/LWgzAHE2W86OymUhEMTqTh77dXEgo0jNTwylESCCF0yO7WmT4JElLn5uvlTBACRJfi7K5jGAQvgdDx1n4vBhFjuublm0QNDE2k4O+1UhEJM47U8cxLDQgecsnu1ZY4BxBR5ubu5EgPAUM= ');
          audio.volume = 0.5;
          audio.play().catch(e => console.log('Não foi possível tocar o som:', e));

          enqueueSnackbar("Novo relatório de treinamento recebido!", { variant: "info" });
        }
      }

      setRelatoriosTreinamento(sortedData);
      setUltimaAtualizacao(new Date());
    } catch (error) {
      console.error("Erro ao carregar relatórios:", error);
      if (!isAutoUpdate) {
        enqueueSnackbar("Erro ao carregar relatórios de treinamento", { variant: "error" });
      }
    } finally {
      if (!isAutoUpdate) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    carregarDados();

    // Atualização automática a cada 30 segundos
    const interval = setInterval(() => {
      carregarDados(true); // true indica que é atualização automática
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  const toggleVisibility = (id: string) => {
    setIsVisible(prevState => ({
      ...prevState,
      [id]: !prevState[id],
    }));
  };

  const handleAceitarRelatorio = async (id: string) => {
    try {
      setLoading(true);
      await api.patch(`/relatorioTreinamento/${id}/aceitar`);
      enqueueSnackbar("Relatório aceito com sucesso!", { variant: "success" });
      carregarDados();
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || "Erro ao aceitar relatório", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleEditarRelatorio = (relatorio: RelatorioTreinamento) => {
    setRelatorioSelecionado(relatorio);
    setEditModalOpen(true);
  };

  const abrirModalErros = (relatorio: RelatorioTreinamento) => {
    setRelatorioSelecionado(relatorio);
    setErrosModalOpen(true);
  };

  const fecharModais = () => {
    setEditModalOpen(false);
    setErrosModalOpen(false);
    setRelatorioSelecionado(null);
  };

  const relatoriosPendentes = relatoriosTreinamento.filter(r => r.status === "P").length;

  return (
    <div className="space-y-4">
      {/* Título e Período */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Relatórios de Treinamento</span>
            <Badge variant="outline" className="text-sm">
              Hoje até amanhã 04:00
            </Badge>
          </CardTitle>
        </CardHeader>
      </Card>

      {/* Estatísticas */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold">{relatoriosTreinamento.length}</div>
            <p className="text-xs text-muted-foreground">Total de Relatórios</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-yellow-600">{relatoriosPendentes}</div>
            <p className="text-xs text-muted-foreground">Pendentes</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-green-600">
              {relatoriosTreinamento.reduce((acc, rel) => acc + rel.qtd_aprovados, 0)}
            </div>
            <p className="text-xs text-muted-foreground">Aprovados Total</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-red-600">
              {relatoriosTreinamento.reduce((acc, rel) => acc + rel.reprovados.length, 0)}
            </div>
            <p className="text-xs text-muted-foreground">Reprovados Total</p>
          </CardContent>
        </Card>
      </div>

      {/* Lista de Relatórios */}
      <div className="space-y-4">
        {loading && relatoriosTreinamento.length === 0 ? (
          <>
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="space-y-2 flex-1">
                    <Skeleton className="h-6 w-48 rounded" />
                    <div className="flex items-center gap-4">
                      <Skeleton className="h-4 w-32 rounded" />
                      <Skeleton className="h-4 w-20 rounded" />
                    </div>
                  </div>
                  <Skeleton className="h-6 w-16 rounded" />
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 gap-2">
                  <Skeleton className="h-4 w-full rounded" />
                  <Skeleton className="h-4 w-full rounded" />
                  <Skeleton className="h-4 w-3/4 rounded" />
                  <Skeleton className="h-4 w-2/3 rounded" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="space-y-2 flex-1">
                    <Skeleton className="h-6 w-40 rounded" />
                    <div className="flex items-center gap-4">
                      <Skeleton className="h-4 w-28 rounded" />
                      <Skeleton className="h-4 w-24 rounded" />
                    </div>
                  </div>
                  <Skeleton className="h-6 w-16 rounded" />
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 gap-2">
                  <Skeleton className="h-4 w-full rounded" />
                  <Skeleton className="h-4 w-full rounded" />
                  <Skeleton className="h-4 w-1/2 rounded" />
                  <Skeleton className="h-4 w-3/4 rounded" />
                </div>
              </CardContent>
            </Card>
          </>
        ) : relatoriosTreinamento.length === 0 ? (
          <Card>
            <CardContent className="pt-6 text-center text-muted-foreground">
              Nenhum relatório encontrado no período
            </CardContent>
          </Card>
        ) : (
          relatoriosTreinamento.map((relatorio, index) => {
            const {
              id,
              inicio_treinamento,
              termino_treinamento,
              treinamento,
              oficial_responsavel,
              criado_em,
              patente_treinador,
              treinador,
              patente_auxiliar,
              auxiliar,
              sala,
              aprovados,
              reprovados,
              qtd_treinados,
              qtd_aprovados,
              observacao,
              status,
              militar_aceitou,
            } = relatorio;

            return (
              <StaggerWrapper key={id} index={index}>
                <Card className="hover:shadow-md transition-shadow">
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div className="space-y-1 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <CardTitle className="text-lg">
                            {treinamento?.treinamento || "Treinamento não especificado"}
                          </CardTitle>
                          {relatorio.tipo_relatorio === "treinamento" && (
                            <Badge className="bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/40 dark:text-blue-300 dark:border-blue-700 text-xs">
                              Treinamento
                            </Badge>
                          )}
                          {relatorio.tipo_relatorio === "teste" && (
                            <Badge className="bg-purple-100 text-purple-800 border-purple-200 dark:bg-purple-900/40 dark:text-purple-300 dark:border-purple-700 text-xs">
                              Teste
                            </Badge>
                          )}
                          {getStatusBadge(status, militar_aceitou)}
                        </div>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {moment(inicio_treinamento).format("DD/MM/YYYY HH:mm")}
                        </span>
                        <span>ID: {id.substring(0, 8)}</span>
                      </div>
                    </div>
                    <Badge className={getStatusColor(qtd_aprovados, reprovados.length)}>
                      {getStatusIcon(qtd_aprovados, reprovados.length)}
                      <span className="ml-1">
                        {qtd_aprovados}/{qtd_treinados}
                      </span>
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-muted-foreground">Oficial: </span>
                      <span className="font-medium">{oficial_responsavel}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Treinador: </span>
                      <span className="font-medium">
                        {patente_treinador} {treinador?.nick}
                      </span>
                    </div>
                    {auxiliar && (
                      <div>
                        <span className="text-muted-foreground">Auxiliar: </span>
                        <span className="font-medium">
                          {patente_auxiliar} {auxiliar?.nick}
                        </span>
                      </div>
                    )}
                    <div>
                      <span className="text-muted-foreground">Sala: </span>
                      <span className="font-medium">{sala}</span>
                    </div>
                  </div>

                  <div className="flex gap-2 flex-wrap">
                    {status === "P" && (
                      <Button
                        variant="default"
                        size="sm"
                        onClick={() => handleAceitarRelatorio(id)}
                        disabled={loading}
                        className="bg-green-600 hover:bg-green-700 text-white"
                      >
                        <CheckCircle className="h-4 w-4 mr-1" />
                        Aceitar Relatório
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEditarRelatorio(relatorio)}
                      className="border-blue-300 text-blue-600 hover:bg-blue-50 dark:border-blue-600 dark:text-blue-400 dark:hover:bg-blue-900/30"
                    >
                      <Edit className="h-4 w-4 mr-1" />
                      Editar
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => abrirModalErros(relatorio)}
                      className="border-orange-300 text-orange-600 hover:bg-orange-50 dark:border-orange-600 dark:text-orange-400 dark:hover:bg-orange-900/30"
                    >
                      <XCircle className="h-4 w-4 mr-1" />
                      Relatar Erros
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => toggleVisibility(id)}
                      className="border-[oklch(0.45_0.15_142)] text-[oklch(0.45_0.15_142)] hover:bg-[oklch(0.45_0.15_142)] hover:text-white"
                    >
                      {isVisible[id] ? (
                        <>
                          <ChevronUp className="h-4 w-4 mr-1" />
                          Ocultar
                        </>
                      ) : (
                        <>
                          <ChevronDown className="h-4 w-4 mr-1" />
                          Ver Detalhes
                        </>
                      )}
                    </Button>
                  </div>

                  {isVisible[id] && (
                    <div className="mt-4 pt-4 border-t">
                      <DetalheRelatorio
                        id={id}
                        inicio_treinamento={inicio_treinamento}
                        termino_treinamento={termino_treinamento}
                        treinamento={treinamento}
                        oficial_responsavel={oficial_responsavel}
                        criado_em={criado_em}
                        patente_treinador={patente_treinador}
                        treinador={treinador}
                        patente_auxiliar={patente_auxiliar}
                        auxiliar={auxiliar}
                        sala={sala}
                        aprovados={aprovados}
                        reprovados={reprovados}
                        qtd_treinados={qtd_treinados}
                        qtd_aprovados={qtd_aprovados}
                        observacao={observacao}
                      />
                    </div>
                  )}
                </CardContent>
              </Card>
            </StaggerWrapper>
            );
          })
        )}
      </div>

      {/* Modais */}
      <EditTreinamentoModal
        isOpen={editModalOpen}
        onClose={fecharModais}
        relatorio={relatorioSelecionado}
        onSuccess={carregarDados}
      />

      <RelatarErrosModal
        isOpen={errosModalOpen}
        onClose={fecharModais}
        relatorio={relatorioSelecionado}
        onSuccess={carregarDados}
      />
    </div>
  );
}
